import { useRef } from 'react'
import { CartProductOptionType } from '@/containers/types/CartProductOption'
import { CartShippingGroupType } from '@/containers/types/CartShippingGroup'
import { SkuCodeQuantityType, SkuCodeQuantityTypeUtil } from '@/containers/types/SkuCodeQuantity'

export type UseSkuCodeQuantityValidatorType = {
  setSkuCodeQuantity: (newSkuCodeQuantity: SkuCodeQuantityType) => void
  setSkuCodeQuantityByCartShippingGroups: (cartShippingGroups: CartShippingGroupType[]) => void
  setSkuCodeQuantityByCartProductOptions: (cartProductOptions: CartProductOptionType[]) => void
  clearSkuCodeQuantity: () => void
  setSkuCodeQuantityCandidate: (newSkuCodeQuantityCandidate: SkuCodeQuantityType) => void
  setSkuCodeQuantityCandidateByCartShippingGroups: (cartShippingGroups: CartShippingGroupType[]) => void
  setSkuCodeQuantityCandidateByCartProductOptions: (cartProductOptions: CartProductOptionType[]) => void
  clearSkuCodeQuantityCandidate: () => void
  getAvailablePurchaseQuantity: (
    cartProductOption: CartProductOptionType,
    extendPurchaseQuantity?: number,
    withCandidate?: boolean
  ) => number
  isValidPurchaseQuantity: (
    cartProductOption: CartProductOptionType,
    newPurchaseQuantity?: number,
    withCandidate?: boolean
  ) => boolean
  isSkuCodeContains: (cartProductOption: CartProductOptionType, withCandidate?: boolean) => boolean
}

const useSkuCodeQuantityValidator = (): UseSkuCodeQuantityValidatorType => {
  const skuCodeQuantity = useRef<SkuCodeQuantityType>({})
  const skuCodeQuantityCandidate = useRef<SkuCodeQuantityType>({})

  const setSkuCodeQuantity = (newSkuCodeQuantity: SkuCodeQuantityType) => {
    SkuCodeQuantityTypeUtil.set(skuCodeQuantity.current, newSkuCodeQuantity)
  }
  const setSkuCodeQuantityByCartShippingGroups = (cartShippingGroups: CartShippingGroupType[]) => {
    const newSkuCodeQuantity = SkuCodeQuantityTypeUtil.makeSkuCodeQuantityByCartShippingGroups(cartShippingGroups)
    SkuCodeQuantityTypeUtil.set(skuCodeQuantity.current, newSkuCodeQuantity)
  }
  const setSkuCodeQuantityByCartProductOptions = (cartProductOptions: CartProductOptionType[]) => {
    const newSkuCodeQuantity = SkuCodeQuantityTypeUtil.makeSkuCodeQuantityByCartProductOptions(cartProductOptions)
    SkuCodeQuantityTypeUtil.set(skuCodeQuantity.current, newSkuCodeQuantity)
  }
  const clearSkuCodeQuantity = () => {
    SkuCodeQuantityTypeUtil.clear(skuCodeQuantity.current)
  }
  const setSkuCodeQuantityCandidate = (newSkuCodeQuantity: SkuCodeQuantityType) => {
    SkuCodeQuantityTypeUtil.set(skuCodeQuantityCandidate.current, newSkuCodeQuantity)
  }
  const setSkuCodeQuantityCandidateByCartShippingGroups = (cartShippingGroups: CartShippingGroupType[]) => {
    const newSkuCodeQuantity = SkuCodeQuantityTypeUtil.makeSkuCodeQuantityByCartShippingGroups(cartShippingGroups)
    SkuCodeQuantityTypeUtil.set(skuCodeQuantityCandidate.current, newSkuCodeQuantity)
  }
  const setSkuCodeQuantityCandidateByCartProductOptions = (cartProductOptions: CartProductOptionType[]) => {
    const newSkuCodeQuantity = SkuCodeQuantityTypeUtil.makeSkuCodeQuantityByCartProductOptions(cartProductOptions)
    SkuCodeQuantityTypeUtil.set(skuCodeQuantityCandidate.current, newSkuCodeQuantity)
  }
  const clearSkuCodeQuantityCandidate = () => {
    SkuCodeQuantityTypeUtil.clear(skuCodeQuantityCandidate.current)
  }

  const getAvailablePurchaseQuantity = (
    cartProductOption: CartProductOptionType,
    extendPurchaseQuantity?: number,
    withCandidate?: boolean
  ): number => {
    let availablePurchaseQuantityMin = undefined
    for (const productOptionSku of cartProductOption.productOptionSkus) {
      const availablePurchaseStockQuantity = SkuCodeQuantityTypeUtil.getAvailablePurchaseStockQuantity(
        productOptionSku.sku.code,
        skuCodeQuantity.current,
        withCandidate ? skuCodeQuantityCandidate.current : undefined,
        extendPurchaseQuantity
      )
      const availablePurchaseQuantity =
        availablePurchaseStockQuantity === undefined
          ? undefined
          : Math.floor(availablePurchaseStockQuantity / productOptionSku.count)
      if (availablePurchaseQuantity !== undefined) {
        if (availablePurchaseQuantityMin === undefined) {
          availablePurchaseQuantityMin = availablePurchaseQuantity
        } else {
          availablePurchaseQuantityMin = Math.min(availablePurchaseQuantityMin, availablePurchaseQuantity)
        }
        if (availablePurchaseQuantityMin < 0) {
          availablePurchaseQuantityMin = 0
        }
      }
    }
    if (availablePurchaseQuantityMin === undefined) {
      availablePurchaseQuantityMin = cartProductOption.stockQuantity
    }
    return availablePurchaseQuantityMin
  }

  const isValidPurchaseQuantity = (
    cartProductOption: CartProductOptionType,
    newPurchaseQuantity: number = cartProductOption.purchaseQuantity,
    withCandidate?: boolean
  ): boolean => {
    const diffQuantity = newPurchaseQuantity - cartProductOption.purchaseQuantity
    for (const productOptionSku of cartProductOption.productOptionSkus) {
      const availablePurchaseStockQuantity = SkuCodeQuantityTypeUtil.getAvailablePurchaseStockQuantity(
        productOptionSku.sku.code,
        skuCodeQuantity.current,
        withCandidate ? skuCodeQuantityCandidate.current : undefined,
        diffQuantity * productOptionSku.count
      )
      if (!!availablePurchaseStockQuantity && availablePurchaseStockQuantity < 0) {
        return false
      }
    }
    return true
  }

  const isSkuCodeContains = (cartProductOption: CartProductOptionType, withCandidate?: boolean): boolean => {
    if (SkuCodeQuantityTypeUtil.isSkuCodeContains(skuCodeQuantity.current, cartProductOption)) {
      return true
    }
    if (withCandidate) {
      return SkuCodeQuantityTypeUtil.isSkuCodeContains(skuCodeQuantityCandidate.current, cartProductOption)
    }
    return false
  }

  return {
    setSkuCodeQuantity,
    setSkuCodeQuantityByCartShippingGroups,
    setSkuCodeQuantityByCartProductOptions,
    clearSkuCodeQuantity,
    setSkuCodeQuantityCandidate,
    setSkuCodeQuantityCandidateByCartShippingGroups,
    setSkuCodeQuantityCandidateByCartProductOptions,
    clearSkuCodeQuantityCandidate,
    getAvailablePurchaseQuantity,
    isValidPurchaseQuantity,
    isSkuCodeContains,
  }
}

export default useSkuCodeQuantityValidator
